import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

import BorderedButton from '../../common/buttons/BorderedButton';
import PrimaryButton from '../../common/buttons/PrimaryButton';
import AppSnackbar from '../../common/popups/AppSnackbar';
import { useMasterData } from '../../../contexts/MasterDataContext';
import AvatarSection from './sections/AvatarSection';
import AccountSection from './sections/AccountSection';
import ProfileSection from './sections/ProfileSection';
import TeachingSection from './sections/TeachingSection';
import QualificationsSection from './sections/QualificationsSection';
import TimeSlotsSection from './sections/TimeSlotsSection';
import ReviewsSection from './sections/ReviewsSection';
import RecommendationsSection from './sections/RecommendationsSection';
import { rules } from '../../../validations/rules';

const DEFAULT_TIME_SLOTS = {
  SUN: [],
  MON: [],
  TUE: [],
  WED: [],
  THU: [],
  FRI: [],
  SAT: [],
};

export default function TeacherForm({ mode = 'create', initialData = null, onSubmit }) {
  const navigate = useNavigate();
  const { subjectOptions, mediumOptions, cityOptions, classTypeOptions } = useMasterData();

  const isEdit = mode === 'edit';

  const [loading, setLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(initialData?.avatar ?? null);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [formData, setFormData] = useState({
    firstName: initialData?.firstName ?? '',
    lastName: initialData?.lastName ?? '',
    email: initialData?.email ?? '',
    phone: initialData?.phone ?? '',
    password: '',
    dob: initialData?.dob ?? '',
    bio: initialData?.bio ?? '',
    introVideoUrl: initialData?.videoUrl ?? '',
    subjectCategory: initialData?.subjectCategory ?? '',
    subjects: initialData?.subjects?.map((s) => (typeof s === 'object' ? s.id : s)) ?? [],
    medium: initialData?.medium?.map((m) => (typeof m === 'object' ? m.id : m)) ?? [],
    classTypes: initialData?.classTypes?.map((t) => (typeof t === 'object' ? t.id : t)) ?? [],
    cities: initialData?.cities?.map((c) => (typeof c === 'object' ? c.id : c)) ?? [],
  });

  const [qualifications, setQualifications] = useState(initialData?.qualifications ?? []);
  const [timeSlots, setTimeSlots] = useState(initialData?.timeSlots ?? DEFAULT_TIME_SLOTS);
  const [reviews, setReviews] = useState(initialData?.reviews ?? []);
  const [recommendations, setRecommendations] = useState(initialData?.recommendations ?? []);

  const handleQualificationsChange = useCallback((q) => setQualifications(q), []);
  const handleTimeSlotsChange = useCallback((ts) => setTimeSlots(ts), []);
  const handleReviewsChange = useCallback((r) => setReviews(r), []);
  const handleRecommendationsChange = useCallback((r) => setRecommendations(r), []);

  const handleFormChange = useCallback((patch) => {
    setFormData((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const phoneError = rules.phone(formData.phone);
    if (phoneError) {
      setErrors({ phone: phoneError });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const payload = new FormData();

      payload.append('firstName', formData.firstName);
      payload.append('lastName', formData.lastName);
      payload.append('email', formData.email);
      payload.append('phone', formData.phone);
      payload.append('dob', formData.dob ?? '');
      payload.append('bio', formData.bio);
      payload.append('introVideoUrl', formData.introVideoUrl ?? '');
      payload.append('subjectCategory', formData.subjectCategory ?? '');

      if (formData.password) payload.append('password', formData.password);

      formData.subjects.forEach((id) => payload.append('subjects[]', id));
      formData.medium.forEach((id) => payload.append('medium[]', id));
      formData.classTypes.forEach((id) => payload.append('classTypes[]', id));
      formData.cities.forEach((id) => payload.append('cities[]', id));

      payload.append('qualifications', JSON.stringify(qualifications));
      payload.append('timeSlots', JSON.stringify(timeSlots));
      payload.append('reviews', JSON.stringify(reviews));
      payload.append('recommendations', JSON.stringify(recommendations));

      if (avatarFile) payload.append('avatar', avatarFile);

      await onSubmit(payload);

      setSnackbar({
        open: true,
        message: isEdit ? 'Teacher updated successfully!' : 'Teacher created successfully!',
        severity: 'success',
      });

      navigate('/admin/teachers');
    } catch (err) {
      const messages = err.response?.data?.errors;
      if (messages) {
        const flat = {};
        Object.entries(messages).forEach(([k, v]) => (flat[k] = v[0]));
        setErrors(flat);
      }
      setSnackbar({
        open: true,
        message: isEdit ? 'Failed to update teacher.' : 'Failed to create teacher.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-6 mb-8">
        <div className="text-left">
          <h1 className="text-3xl font-bold text-light">
            {isEdit ? 'Edit Teacher' : 'Create Teacher'}
          </h1>
          <p className="text-gray-400 mt-1">
            {isEdit
              ? 'Update teacher profile details'
              : 'Add a new teacher profile to the platform'}
          </p>
        </div>
        <BorderedButton className="w-full sm:w-auto" onClick={() => navigate('/admin/teachers')}>
          Cancel
        </BorderedButton>
      </div>

      <div className="w-full sm:w-3/4 mx-auto">
        {/* Validation errors summary */}
        {Object.keys(errors).length > 0 && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 font-semibold text-sm mb-2">
              Please fix the following errors:
            </p>
            <ul className="list-disc list-inside space-y-1">
              {Object.values(errors).map((msg, i) => (
                <li key={i} className="text-red-400 text-sm">
                  {msg}
                </li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <AvatarSection
            avatarPreview={avatarPreview}
            firstName={formData.firstName}
            onAvatarChange={(file, preview) => {
              setAvatarFile(file);
              setAvatarPreview(preview);
            }}
          />

          <AccountSection
            formData={formData}
            errors={errors}
            isEdit={isEdit}
            onChange={handleFormChange}
          />

          <ProfileSection formData={formData} errors={errors} onChange={handleFormChange} />

          <TeachingSection
            formData={formData}
            errors={errors}
            subjectOptions={subjectOptions}
            mediumOptions={mediumOptions}
            classTypeOptions={classTypeOptions}
            cityOptions={cityOptions}
            onChange={handleFormChange}
          />

          <QualificationsSection
            qualifications={qualifications}
            onChange={handleQualificationsChange}
          />

          <TimeSlotsSection
            timeSlots={timeSlots}
            setTimeSlots={handleTimeSlotsChange}
            subjectOptions={subjectOptions}
            mediumOptions={mediumOptions}
            cityOptions={cityOptions}
            classTypeOptions={classTypeOptions}
            selectedSubjects={formData.subjects}
            selectedMedium={formData.medium}
            selectedCities={formData.cities}
            selectedClassTypes={formData.classTypes}
          />

          <ReviewsSection reviews={reviews} onChange={handleReviewsChange} />

          <RecommendationsSection
            recommendations={recommendations}
            onChange={handleRecommendationsChange}
          />

          {/* Submit */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 pb-10">
            <BorderedButton onClick={() => navigate('/admin/teachers')}>Cancel</BorderedButton>
            <PrimaryButton type="submit" disabled={loading}>
              {loading ? (
                <span className="flex items-center gap-2">
                  <CircularProgress size={18} sx={{ color: 'white' }} />
                  {isEdit ? 'Saving...' : 'Creating...'}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {isEdit ? (
                    <>
                      <SaveOutlinedIcon fontSize="small" /> Save Changes
                    </>
                  ) : (
                    <>
                      <PersonAddAltOutlinedIcon fontSize="small" /> Create Teacher
                    </>
                  )}
                </span>
              )}
            </PrimaryButton>
          </div>
        </form>
      </div>

      <AppSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </div>
  );
}
